'use client';

import { Box, Skeleton, Typography } from '@mui/material';

import { EmbeddedMap } from '@/components/atoms';
import { ContactPageContactInfo } from '@/services/types/contact-page';

interface ContactInfoSectionProps {
  contactInfo?: ContactPageContactInfo;
  mapUrl?: string;
  loading?: boolean;
}

export default function ContactInfoSection({
  contactInfo,
  mapUrl,
  loading = false,
}: ContactInfoSectionProps) {
  // Fallback data if no data is provided
  const fallbackContactInfo: ContactPageContactInfo = {
    id: 1,
    address:
      'Trụ sở: Thôn Rùa Thượng, Xã Thanh Thùy, Huyện Thanh Oai, Thành phố Hà Nội, Việt Nam',
    phone: '0983973023',
    email: 'contact@bachviet.com.vn',
  };

  const fallbackMapUrl =
    'https://maps.google.com/maps?q=Trung+T%C3%A2m+H%E1%BB%99i+Ngh%E1%BB%8B+Ti%E1%BB%87c+C%C6%B0%E1%BB%9Bi+Sen+H%E1%BB%93ng';

  const currentContactInfo = contactInfo || fallbackContactInfo;
  const currentMapUrl = mapUrl || fallbackMapUrl;

  if (loading) {
    return (
      <Box
        sx={{
          width: '100%',
          maxWidth: '536px',
          backgroundColor: 'white',
        }}
      >
        {/* Contact Information Skeleton */}
        <Box sx={{ mb: 4 }}>
          <Skeleton variant='text' width='100%' height={24} sx={{ mb: 2 }} />
          <Skeleton variant='text' width='80%' height={20} sx={{ mb: 1 }} />
          <Skeleton variant='text' width='60%' height={20} sx={{ mb: 1 }} />
        </Box>

        {/* Map/Image Skeleton */}
        <Skeleton
          variant='rectangular'
          width='100%'
          height={286}
          sx={{ borderRadius: '8px' }}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '536px',
        backgroundColor: 'white',
      }}
    >
      {/* Contact Information */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant='body1'
          sx={{
            fontFamily: 'SF Pro, sans-serif',
            fontSize: '16px',
            lineHeight: 1.5,
            color: '#333333',
            whiteSpace: 'pre-line',
            mb: 3,
          }}
        >
          <strong>Trụ sở:</strong> {currentContactInfo.address}
        </Typography>

        <Typography
          variant='body1'
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontSize: '16px',
            lineHeight: 1.21,
            color: '#333333',
            mb: 3,
          }}
        >
          <strong>Tel:</strong> {currentContactInfo.phone}
        </Typography>

        <Typography
          variant='body1'
          sx={{
            fontFamily: 'IBM Plex Sans, sans-serif',
            fontSize: '16px',
            lineHeight: 1.3,
            color: '#333333',
            mb: 3,
          }}
        >
          <strong>Email:</strong> {currentContactInfo.email}
        </Typography>
      </Box>

      {/* Map Link or Office Image */}
      <Box
        sx={{
          width: '100%',
          height: '286px',
          borderRadius: '8px',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <EmbeddedMap mapUrl={currentMapUrl} width='100%' height='100%' />
      </Box>
    </Box>
  );
}
